import { Instance, isAlive, SnapshotOut, types } from 'mobx-state-tree';
import { buildSenderName, isOwnSender } from '../utils';

const JUST_ADDED_RESET_MS = 500;

const MessageSenderModel = types.model('MessageSenderModel', {
  max_user_id: types.optional(types.maybeNull(types.number), null),
  first_name: types.optional(types.maybeNull(types.string), null),
  last_name: types.optional(types.maybeNull(types.string), null),
  is_bot: types.optional(types.boolean, false),
});

const RepostSenderModel = types.model('RepostSenderModel', {
  user_id: types.optional(types.maybeNull(types.number), null),
  first_name: types.optional(types.maybeNull(types.string), null),
  last_name: types.optional(types.maybeNull(types.string), null),
  username: types.optional(types.maybeNull(types.string), null),
  name: types.optional(types.maybeNull(types.string), null),
  is_bot: types.optional(types.boolean, false),
});

const MessageAttachmentModel = types.model('MessageAttachmentModel', {
  id: types.string,
  type: types.optional(types.string, ''),
  url: types.optional(types.maybeNull(types.string), null),
  filename: types.optional(types.maybeNull(types.string), null),
  size: types.optional(types.maybeNull(types.number), null),
  thumbnailUrl: types.optional(types.maybeNull(types.string), null),
  duration: types.optional(types.maybeNull(types.number), null),
});

export type TMessageAttachmentSnapshot = SnapshotOut<
  typeof MessageAttachmentModel
>;

const SystemMessageEntityModel = types.model('SystemMessageEntityModel', {
  type: types.enumeration<'request' | 'appeal'>('SystemEntityType', [
    'request',
    'appeal',
  ]),
  id: types.string,
  number: types.optional(types.maybeNull(types.string), null),
  created_at: types.optional(types.maybeNull(types.string), null),
});

const SystemMessageModel = types.model('SystemMessageModel', {
  event_type: types.string,
  entity: types.optional(types.maybeNull(SystemMessageEntityModel), null),
});

export type TSystemMessageEntity = Instance<typeof SystemMessageEntityModel>;

export const ChatMessageModel = types
  .model('ChatMessageModel', {
    id: types.string,
    max_message_id: types.string,
    text: types.optional(types.maybeNull(types.string), null),
    created_at: types.string,
    sender: types.optional(types.maybeNull(MessageSenderModel), null),
    system_sender: types.optional(types.maybeNull(MessageSenderModel), null),
    has_attachments: types.optional(types.boolean, false),
    attachments: types.optional(types.array(MessageAttachmentModel), []),
    is_edited: types.optional(types.boolean, false),
    is_deleted: types.optional(types.boolean, false),
    is_massive: types.optional(types.boolean, false),
    is_repost: types.optional(types.boolean, false),
    repost_sender: types.optional(types.maybeNull(RepostSenderModel), null),
    repost_source_chat_id: types.optional(types.maybeNull(types.number), null),
    // Только у сообщений массовой рассылки (GetMassiveMessages): chat_id ->
    // max_message_id реального сообщения в этом чате. Нужен, чтобы резолвить
    // вложения (GetAttachment) по настоящим chat_id/messageId, а не по заглушке.
    chats: types.optional(types.frozen<Record<string, string>>(), {}),
    reply_to_max_id: types.optional(types.maybeNull(types.string), null),
    reply_preview: types.optional(
      types.maybeNull(
        types.model({
          sender_name: types.optional(types.string, ''),
          preview: types.optional(types.string, ''),
          is_deleted: types.optional(types.boolean, false),
        })
      ),
      null
    ),
    sendStatus: types.optional(
      types.maybeNull(
        types.enumeration<'sending' | 'error'>('MessageSendStatus', [
          'sending',
          'error',
        ])
      ),
      null
    ),
    system_message: types.optional(types.maybeNull(SystemMessageModel), null),
  })
  .volatile(() => ({
    justAdded: false,
  }))
  .views((self) => ({
    get isOwn(): boolean {
      return isOwnSender(self.system_sender);
    },
    get senderName(): string | null {
      return (
        buildSenderName(self.system_sender) ??
        buildSenderName(self.sender) ??
        null
      );
    },
    get repostSenderName(): string | null {
      const sender = self.repost_sender;

      if (!sender) {
        return null;
      }

      return (
        sender.name ||
        [sender.first_name, sender.last_name]
          .filter(Boolean)
          .join(' ')
          .trim() ||
        sender.username ||
        null
      );
    },
    // Первый чат рассылки — бабл рендерит вложения одного сообщения, а не
    // всех копий сразу, поэтому резолвим GetAttachment только по нему.
    get primaryChatId(): number | undefined {
      const chatId = Object.keys(self.chats)[0];

      return chatId === undefined ? undefined : Number(chatId);
    },
  }))
  .actions((self) => ({
    applyEdit(text: string | null, attachments: TMessageAttachmentSnapshot[]) {
      self.text = text;
      self.attachments.replace(attachments);
      self.has_attachments = attachments.length > 0;
      self.is_edited = true;
    },
    editText(text: string) {
      self.text = text;
      self.is_edited = true;
    },
    markDeleted() {
      self.is_deleted = true;
      self.text = null;
      self.attachments.clear();
      self.has_attachments = false;
    },
    setSendStatus(status: 'sending' | 'error' | null) {
      self.sendStatus = status;
    },
    confirmSent(realMaxMessageId: string) {
      self.max_message_id = realMaxMessageId;
      self.sendStatus = null;
    },
    clearJustAdded() {
      self.justAdded = false;
    },
    flagJustAdded() {
      self.justAdded = true;

      setTimeout(() => {
        if (isAlive(self)) {
          this.clearJustAdded();
        }
      }, JUST_ADDED_RESET_MS);
    },
  }));

export type TChatMessage = Instance<typeof ChatMessageModel>;
